import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Brain, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  RefreshCw, Play, X, Check, ChevronDown, ChevronRight,
  DollarSign, Target, Package, Clock, Zap, AlertCircle,
  BarChart3, Filter
} from 'lucide-react'
import { googleAdsApi, GoogleAdsRecommendation } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

// Formatting helpers
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

// Priority styles
const priorityStyles: Record<string, { bg: string; text: string; border: string; icon: React.ReactNode }> = {
  CRITICAL: {
    bg: 'bg-danger-100 dark:bg-danger-900/30',
    text: 'text-red-800 dark:text-red-300',
    border: 'border-red-300 dark:border-red-700',
    icon: <AlertCircle className="w-4 h-4" />
  },
  HIGH: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-800 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-700',
    icon: <AlertTriangle className="w-4 h-4" />
  },
  MEDIUM: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-800 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
    icon: <TrendingUp className="w-4 h-4" />
  },
  LOW: {
    bg: 'bg-slate-100 dark:bg-slate-800/30',
    text: 'text-gray-700 dark:text-slate-400',
    border: 'border-slate-300 dark:border-gray-700',
    icon: <Zap className="w-4 h-4" />
  }
}

// Recommendation type icons
const typeIcons: Record<string, React.ReactNode> = {
  budget_increase: <TrendingUp className="w-4 h-4 text-success-500" />,
  budget_decrease: <TrendingDown className="w-4 h-4 text-danger-500" />,
  campaign_pause: <X className="w-4 h-4 text-danger-500" />,
  campaign_enable: <Play className="w-4 h-4 text-success-500" />,
  bid_increase: <DollarSign className="w-4 h-4 text-success-500" />,
  bid_decrease: <DollarSign className="w-4 h-4 text-danger-500" />,
  product_add: <Package className="w-4 h-4 text-primary-500" />,
  product_pause: <Package className="w-4 h-4 text-orange-500" />,
  product_exclude: <Package className="w-4 h-4 text-danger-500" />,
  day_of_week_adjust: <Clock className="w-4 h-4 text-purple-500" />,
  campaign_restructure: <Target className="w-4 h-4 text-indigo-500" />,
}

const typeLabels: Record<string, string> = {
  budget_increase: 'Aumentar presupuesto',
  budget_decrease: 'Reducir presupuesto',
  budget_redistribute: 'Redistribuir presupuesto',
  campaign_pause: 'Pausar campana',
  campaign_enable: 'Activar campana',
  campaign_restructure: 'Reestructurar',
  bid_increase: 'Aumentar puja',
  bid_decrease: 'Reducir puja',
  bid_strategy_change: 'Cambiar estrategia',
  product_add: 'Anadir producto',
  product_pause: 'Pausar producto',
  product_exclude: 'Excluir producto',
  product_priority_increase: 'Priorizar producto',
  audience_bid_adjust: 'Ajustar audiencia',
  audience_add: 'Anadir audiencia',
  daypart_adjust: 'Ajustar horario',
  day_of_week_adjust: 'Ajustar dia semana',
  device_bid_adjust: 'Ajustar dispositivo',
  location_bid_adjust: 'Ajustar ubicacion',
}

// Summary Card Component
function SummaryCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'blue'
}: {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  trend?: 'up' | 'down' | 'neutral'
  color?: 'blue' | 'green' | 'red' | 'orange' | 'purple'
}) {
  const colorStyles = {
    blue: 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400',
    green: 'bg-success-50 dark:bg-success-900/20 text-success-600 dark:text-success-400',
    red: 'bg-danger-50 dark:bg-danger-900/20 text-danger-600 dark:text-danger-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
            {value}
          </p>
          {subtitle && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={cn('p-3 rounded-lg', colorStyles[color])}>
          {icon}
        </div>
      </div>
      {trend && (
        <div className="mt-2 flex items-center">
          {trend === 'up' && <TrendingUp className="w-4 h-4 text-success-500 mr-1" />}
          {trend === 'down' && <TrendingDown className="w-4 h-4 text-danger-500 mr-1" />}
          {trend === 'neutral' && <span className="w-4 h-4 mr-1">-</span>}
        </div>
      )}
    </div>
  )
}

// Recommendation Card Component
function RecommendationCard({
  recommendation,
  onApprove,
  onReject,
  onExecute,
  isProcessing
}: {
  recommendation: GoogleAdsRecommendation
  onApprove: () => void
  onReject: () => void
  onExecute: () => void
  isProcessing: boolean
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const priority = priorityStyles[recommendation.priority_label] || priorityStyles.LOW
  const typeIcon = typeIcons[recommendation.type] || <Target className="w-4 h-4" />
  const typeLabel = typeLabels[recommendation.type] || recommendation.type

  return (
    <div className={cn(
      'bg-white dark:bg-slate-800 rounded-lg border transition-all duration-200',
      priority.border,
      'hover:shadow-md'
    )}>
      {/* Header */}
      <div
        className="p-4 cursor-pointer flex items-start gap-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Priority Badge */}
        <div className={cn(
          'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium shrink-0',
          priority.bg,
          priority.text
        )}>
          {priority.icon}
          {recommendation.priority_label}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {typeIcon}
            <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-400">
              {typeLabel}
            </span>
          </div>
          <h3 className="font-medium text-slate-900 dark:text-white mt-1 truncate">
            {recommendation.title}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
            {recommendation.description}
          </p>
        </div>

        {/* Impact & Expand */}
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="text-sm text-slate-500 dark:text-slate-400">Impacto mensual</div>
            <div className={cn(
              'font-bold',
              recommendation.estimated_impact_monthly > 0
                ? 'text-success-600 dark:text-success-400'
                : 'text-slate-600 dark:text-slate-400'
            )}>
              {recommendation.estimated_impact_monthly > 0 ? '+' : ''}
              {formatCurrency(recommendation.estimated_impact_monthly)}
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          {/* Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Valor actual</div>
              <div className="font-medium text-slate-900 dark:text-white">
                {String(recommendation.current_value)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Valor recomendado</div>
              <div className="font-medium text-primary-600 dark:text-primary-400">
                {String(recommendation.recommended_value)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Confianza</div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full',
                      recommendation.confidence_score >= 80 ? 'bg-success-500' :
                      recommendation.confidence_score >= 60 ? 'bg-blue-500' :
                      recommendation.confidence_score >= 40 ? 'bg-yellow-500' : 'bg-danger-500'
                    )}
                    style={{ width: `${recommendation.confidence_score}%` }}
                  />
                </div>
                <span className="text-sm font-medium">
                  {Math.round(recommendation.confidence_score)}%
                </span>
              </div>
            </div>
            {recommendation.campaign_name && (
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Campana</div>
                <div className="font-medium text-slate-900 dark:text-white truncate">
                  {recommendation.campaign_name}
                </div>
              </div>
            )}
            {recommendation.product_reference && (
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Producto</div>
                <div className="font-medium text-slate-900 dark:text-white">
                  {recommendation.product_reference}
                </div>
              </div>
            )}
          </div>

          {/* Supporting Data */}
          {recommendation.supporting_data && Object.keys(recommendation.supporting_data).length > 0 && (
            <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">Datos de soporte</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {Object.entries(recommendation.supporting_data).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">
                      {key.replace(/_/g, ' ')}:
                    </span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {typeof value === 'number'
                        ? value % 1 === 0 ? value : value.toFixed(2)
                        : typeof value === 'object'
                          ? JSON.stringify(value).slice(0, 30) + '...'
                          : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {recommendation.status === 'pending' && (
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onReject(); }}
                disabled={isProcessing}
                className="px-3 py-1.5 text-sm font-medium text-danger-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4 inline mr-1" />
                Rechazar
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onApprove(); }}
                disabled={isProcessing}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4 inline mr-1" />
                Aprobar
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onExecute(); }}
                disabled={isProcessing}
                className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {isProcessing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Ejecutar
              </button>
            </div>
          )}

          {recommendation.status !== 'pending' && (
            <div className={cn(
              'text-sm px-3 py-2 rounded-lg inline-flex items-center gap-2',
              recommendation.status === 'applied' ? 'bg-success-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
              recommendation.status === 'rejected' ? 'bg-danger-100 text-red-800 dark:bg-danger-900/30 dark:text-red-300' :
              recommendation.status === 'approved' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
              'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
            )}>
              {recommendation.status === 'applied' && <CheckCircle className="w-4 h-4" />}
              {recommendation.status === 'rejected' && <X className="w-4 h-4" />}
              {recommendation.status === 'approved' && <Check className="w-4 h-4" />}
              Estado: {recommendation.status}
              {recommendation.applied_at && (
                <span className="text-xs opacity-75">
                  ({new Date(recommendation.applied_at).toLocaleDateString()})
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Main Page Component
export default function GoogleAdsOptimizerPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [analyzingDays, setAnalyzingDays] = useState(30)
  const [filterStatus, setFilterStatus] = useState<string>('pending')
  const [filterPriority, setFilterPriority] = useState<number | null>(null)
  const [filterType, setFilterType] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<number | null>(null)

  // Query for summary
  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['google-ads-optimizer-summary'],
    queryFn: googleAdsApi.getOptimizationSummary,
    refetchInterval: 60000, // Refresh every minute
  })

  // Query for recommendations
  const { data: recommendationsData, isLoading: loadingRecommendations, refetch: refetchRecommendations } = useQuery({
    queryKey: ['google-ads-recommendations', filterStatus, filterPriority, filterType],
    queryFn: () => googleAdsApi.getOptimizationRecommendations({
      status: filterStatus as 'pending' | 'approved' | 'rejected' | 'applied' | undefined,
      priority: filterPriority as 1 | 2 | 3 | 4 | undefined,
      recommendation_type: filterType || undefined,
      limit: 100
    }),
  })

  // Mutation for analysis
  const analyzeMutation = useMutation({
    mutationFn: (days: number) => googleAdsApi.analyzeOptimizations(days),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-optimizer-summary'] })
      queryClient.invalidateQueries({ queryKey: ['google-ads-recommendations'] })
      toast.success(
        'Analisis completado',
        `Se generaron ${data.total_recommendations} recomendaciones con un impacto potencial de ${formatCurrency(data.estimated_monthly_impact)}/mes`
      )
    },
    onError: (error: Error) => {
      toast.error('Error en analisis', error.message)
    }
  })

  // Mutations for recommendation actions
  const approveMutation = useMutation({
    mutationFn: googleAdsApi.approveRecommendation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['google-ads-optimizer-summary'] })
      toast.success('Recomendacion aprobada')
    },
    onError: (error: Error) => {
      toast.error('Error', error.message)
    },
    onSettled: () => setProcessingId(null)
  })

  const rejectMutation = useMutation({
    mutationFn: googleAdsApi.rejectRecommendation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['google-ads-optimizer-summary'] })
      toast.success('Recomendacion rechazada')
    },
    onError: (error: Error) => {
      toast.error('Error', error.message)
    },
    onSettled: () => setProcessingId(null)
  })

  const executeMutation = useMutation({
    mutationFn: googleAdsApi.executeRecommendation,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['google-ads-optimizer-summary'] })
      if (data.success) {
        toast.success('Recomendacion ejecutada', data.action)
      } else {
        toast.error('Error al ejecutar', data.error)
      }
    },
    onError: (error: Error) => {
      toast.error('Error', error.message)
    },
    onSettled: () => setProcessingId(null)
  })

  const recommendations = recommendationsData?.recommendations || []

  // Get unique types for filter
  const availableTypes = [...new Set(recommendations.map(r => r.type))]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Brain className="w-7 h-7 text-purple-500" />
            Optimizador de Campanas
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Analisis inteligente y recomendaciones de optimizacion
          </p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={analyzingDays}
            onChange={(e) => setAnalyzingDays(Number(e.target.value))}
            className="px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
          >
            <option value={7}>Ultimos 7 dias</option>
            <option value={14}>Ultimos 14 dias</option>
            <option value={30}>Ultimos 30 dias</option>
            <option value={60}>Ultimos 60 dias</option>
            <option value={90}>Ultimos 90 dias</option>
          </select>
          <button
            onClick={() => analyzeMutation.mutate(analyzingDays)}
            disabled={analyzeMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {analyzeMutation.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Brain className="w-4 h-4" />
            )}
            {analyzeMutation.isPending ? 'Analizando...' : 'Analizar'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Recomendaciones Pendientes"
          value={summary?.pending || 0}
          icon={<AlertTriangle className="w-6 h-6" />}
          color="orange"
        />
        <SummaryCard
          title="Impacto Potencial"
          value={formatCurrency(summary?.potential_monthly_impact || 0)}
          subtitle="por mes"
          icon={<TrendingUp className="w-6 h-6" />}
          color="green"
        />
        <SummaryCard
          title="Impacto Realizado"
          value={formatCurrency(summary?.realized_monthly_impact || 0)}
          subtitle="por mes"
          icon={<CheckCircle className="w-6 h-6" />}
          color="blue"
        />
        <SummaryCard
          title="Aplicadas / Rechazadas"
          value={`${summary?.applied || 0} / ${summary?.rejected || 0}`}
          icon={<BarChart3 className="w-6 h-6" />}
          color="purple"
        />
      </div>

      {/* Priority Breakdown */}
      {summary?.by_priority && Object.keys(summary.by_priority).length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            Por Prioridad
          </h3>
          <div className="flex items-center gap-4 flex-wrap">
            {[
              { priority: 1, label: 'CRITICAL', count: summary.by_priority[1] || 0 },
              { priority: 2, label: 'HIGH', count: summary.by_priority[2] || 0 },
              { priority: 3, label: 'MEDIUM', count: summary.by_priority[3] || 0 },
              { priority: 4, label: 'LOW', count: summary.by_priority[4] || 0 },
            ].map(({ priority, label, count }) => {
              const style = priorityStyles[label]
              return count > 0 && (
                <div
                  key={priority}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
                    style.bg,
                    style.text
                  )}
                >
                  {style.icon}
                  <span className="font-medium">{count}</span>
                  <span className="text-xs opacity-75">{label}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filtros</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobadas</option>
            <option value="applied">Aplicadas</option>
            <option value="rejected">Rechazadas</option>
          </select>

          {/* Priority Filter */}
          <select
            value={filterPriority || ''}
            onChange={(e) => setFilterPriority(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-1.5 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
          >
            <option value="">Todas las prioridades</option>
            <option value="1">CRITICAL</option>
            <option value="2">HIGH</option>
            <option value="3">MEDIUM</option>
            <option value="4">LOW</option>
          </select>

          {/* Type Filter */}
          <select
            value={filterType || ''}
            onChange={(e) => setFilterType(e.target.value || null)}
            className="px-3 py-1.5 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
          >
            <option value="">Todos los tipos</option>
            {availableTypes.map(type => (
              <option key={type} value={type}>
                {typeLabels[type] || type}
              </option>
            ))}
          </select>

          <button
            onClick={() => refetchRecommendations()}
            className="p-1.5 text-slate-500 hover:text-gray-700 dark:hover:text-slate-300"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Recommendations List */}
      <div className="space-y-3">
        {loadingRecommendations || loadingSummary ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            <p className="text-slate-500 dark:text-slate-400 mt-2">Cargando recomendaciones...</p>
          </div>
        ) : recommendations.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <Brain className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" />
            <p className="text-slate-500 dark:text-slate-400 mt-2">
              No hay recomendaciones {filterStatus === 'pending' ? 'pendientes' : 'con estos filtros'}
            </p>
            <button
              onClick={() => analyzeMutation.mutate(analyzingDays)}
              disabled={analyzeMutation.isPending}
              className="mt-4 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium"
            >
              Ejecutar nuevo analisis
            </button>
          </div>
        ) : (
          recommendations.map((rec) => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onApprove={() => {
                setProcessingId(rec.id)
                approveMutation.mutate(rec.id)
              }}
              onReject={() => {
                setProcessingId(rec.id)
                rejectMutation.mutate(rec.id)
              }}
              onExecute={() => {
                setProcessingId(rec.id)
                executeMutation.mutate(rec.id)
              }}
              isProcessing={processingId === rec.id}
            />
          ))
        )}
      </div>

      {/* Results Count */}
      {recommendationsData && (
        <div className="text-sm text-slate-500 dark:text-slate-400 text-center">
          Mostrando {recommendations.length} de {recommendationsData.count} recomendaciones
        </div>
      )}
    </div>
  )
}
